import { Loader2, Mic, Square, Volume2, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { api } from "@/lib/api"
import { cn } from "@/lib/utils"
import {
  HANDS_FREE_SILENCE_MS,
  openMicrophone,
  preloadVoice,
  prepareAudio,
  startSpokenTurn,
  type SpokenTurn,
  type TurnAudio,
  type TurnPhase,
  type VoiceStep,
} from "@/lib/voice/spoken-turn"

/**
 * Answer the question on screen out loud.
 *
 * The interviewer's voice reads the question, then listens until the candidate
 * presses Finish. What Google wrote down goes into the answer box - it is not
 * submitted from here. Speech recognition mishears names and numbers, and a
 * score is only fair on words the candidate has seen and accepted.
 *
 * **Hands-free** keeps that rule and removes the clicks around it. The page
 * notices when the candidate has stopped talking (`lib/voice/silence.ts`) and
 * finishes the turn itself; the interview page then shows the words with a
 * short countdown before sending them, which any edit cancels. When the next
 * question arrives it is read aloud straight away. The speaker and microphone
 * stay open for that, from the one click that started it - browsers only let a
 * page make sound after a click - and close when hands-free is switched off or
 * the page is left. Between turns nothing is sent to Google.
 *
 * Shown only when the server says voice is set up, and always next to the
 * text box: typing is never taken away.
 */

const SILENCE_SECONDS = HANDS_FREE_SILENCE_MS / 1000

const PHASE_LABEL: Record<TurnPhase, string> = {
  connecting: "Connecting to the interviewer's voice…",
  asking: "The interviewer is asking the question…",
  listening: "Listening - answer in your own time, then press Finish.",
  finishing: "Writing down your last words…",
  done: "",
}

/**
 * Where the wait between the click and the interviewer's first word went,
 * step by step, written to the browser console (F12 → Console). Measuring,
 * not behaviour: nothing on the page depends on it.
 */
function stopwatch() {
  const started = performance.now()
  let last = started
  const steps: Record<string, string> = {}
  return (step: VoiceStep) => {
    const now = performance.now()
    steps[step] = `${Math.round(now - last)} ms`
    last = now
    if (step === "first sound") {
      steps.total = `${Math.round(now - started)} ms`
      console.info("PRAXA voice start-up", steps)
    }
  }
}

const HANDS_FREE_LISTENING = `Listening - when you stop talking for ${SILENCE_SECONDS} seconds, your answer is finished. Finish sends it now.`

export function VoiceAnswer({
  sessionId,
  question,
  turnKey,
  notice,
  disabled,
  handsFree,
  onHandsFreeChange,
  onTranscript,
}: {
  sessionId: string
  question: string
  /** Changes with every new question, even one with the same text. */
  turnKey: string
  notice: string
  disabled: boolean
  handsFree: boolean
  onHandsFreeChange: (on: boolean) => void
  onTranscript: (text: string, seconds: number) => void
}) {
  const [phase, setPhase] = useState<TurnPhase | null>(null)
  const [heard, setHeard] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [seconds, setSeconds] = useState(0)
  const turn = useRef<SpokenTurn | null>(null)
  // Hands-free only: the speaker and microphone, kept open between questions.
  const kept = useRef<TurnAudio | null>(null)
  // Whether `kept` is open - as state, because the page shows it.
  const [armed, setArmed] = useState(false)
  // The question a turn was last started for, so it is asked once, not again
  // after every re-render.
  const startedFor = useRef<string | null>(null)

  function closeKept() {
    const audio = kept.current
    kept.current = null
    setArmed(false)
    if (!audio) return
    void audio.microphone.close()
    void audio.player.close()
  }

  // Leaving the page, or the question changing, ends any turn in progress.
  useEffect(() => () => turn.current?.cancel(), [question])

  // Leaving the page closes the microphone that hands-free kept open.
  useEffect(() => () => closeKept(), [])

  // Fetch Google's voice library while the question is being read, so the
  // click does not wait for a download. A failure here is ignored: the click
  // tries again and reports it.
  useEffect(() => {
    const timer = window.setTimeout(() => void preloadVoice().catch(() => undefined), 1000)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (phase !== "listening") return
    const started = Date.now()
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 500)
    return () => window.clearInterval(timer)
  }, [phase])

  // Hands-free: a new question is read aloud as soon as it is on screen. The
  // short delay lets the score from the last answer appear first.
  useEffect(() => {
    if (!handsFree || !armed || disabled || startedFor.current === turnKey) return
    const timer = window.setTimeout(() => void begin(), 600)
    return () => window.clearTimeout(timer)
  })

  function stopHandsFree(message: string | null) {
    turn.current?.cancel()
    turn.current = null
    closeKept()
    setPhase(null)
    if (message) setError(message)
    onHandsFreeChange(false)
  }

  async function begin() {
    startedFor.current = turnKey
    setError(null)
    setHeard("")
    setSeconds(0)
    setPhase("connecting")
    // Before anything is awaited: the click is what lets the page make sound.
    // Hands-free reuses the audio that its first click opened.
    const reuse = handsFree ? kept.current : null
    const audio = reuse ?? prepareAudio()
    const lap = stopwatch()
    // In parallel with the microphone and the token, not after them.
    void preloadVoice().catch(() => undefined)
    try {
      if (!reuse) await openMicrophone(audio)
      lap("microphone")
      if (handsFree) {
        kept.current = audio
        setArmed(true)
      }
      const ticket = await api.interview.voice(sessionId, question)
      lap("token")
      turn.current = await startSpokenTurn(
        ticket,
        audio,
        {
          onPhase: setPhase,
          onAnswer: setHeard,
          onError: (message) => {
            turn.current = null
            if (handsFree) {
              stopHandsFree(message)
            } else {
              setError(message)
              setPhase(null)
            }
          },
          onSilence: () => void finish(),
          onTiming: lap,
        },
        { handsFree, keepAudio: handsFree },
      )
    } catch (caught) {
      turn.current = null
      if (handsFree) {
        stopHandsFree((caught as Error).message)
        return
      }
      void audio.player.close()
      void audio.microphone.close()
      setError((caught as Error).message)
      setPhase(null)
    }
  }

  async function finish() {
    const current = turn.current
    if (!current) return
    turn.current = null
    const result = await current.finish()
    setPhase(null)
    if (result.text) {
      onTranscript(result.text, result.seconds)
    } else if (handsFree) {
      stopHandsFree("Nothing was heard, so hands-free has paused. Check the microphone, or type.")
    } else {
      setError("Nothing was heard. Check the microphone, or type your answer.")
    }
  }

  function cancel() {
    if (handsFree) {
      stopHandsFree(null)
      return
    }
    turn.current?.cancel()
    turn.current = null
    setPhase(null)
  }

  function toggleHandsFree(on: boolean) {
    if (!on) {
      stopHandsFree(null)
      return
    }
    setError(null)
    onHandsFreeChange(true)
  }

  if (phase === null || phase === "done") {
    const waiting = handsFree && armed
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => void begin()}
            aria-describedby="voice-notice"
          >
            <Mic aria-hidden /> {handsFree ? "Start hands-free" : "Answer out loud"}
          </Button>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={handsFree}
              onChange={(event) => toggleHandsFree(event.target.checked)}
              className="size-3.5 accent-primary"
            />
            Hands-free
          </label>
        </div>
        {waiting ? (
          <p className="text-xs text-muted-foreground">
            Hands-free is on: the next question will be read aloud by itself.
          </p>
        ) : handsFree ? (
          <p className="text-xs text-muted-foreground">
            Press Start once. Each answer ends when you stop talking for {SILENCE_SECONDS}{" "}
            seconds, you get a few seconds to check it, and the next question follows.
          </p>
        ) : null}
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        <p id="voice-notice" className="text-[11px] leading-relaxed text-muted-foreground">
          {notice}
        </p>
      </div>
    )
  }

  const listening = phase === "listening"
  return (
    <div
      className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-4"
      role="status"
      aria-live="polite"
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        {listening ? (
          <span className="relative flex size-2.5" aria-hidden>
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-destructive/60" />
            <span className="relative inline-flex size-2.5 rounded-full bg-destructive" />
          </span>
        ) : phase === "asking" ? (
          <Volume2 className="size-4 text-primary" aria-hidden />
        ) : (
          <Loader2 className="size-4 animate-spin text-primary" aria-hidden />
        )}
        {listening && handsFree ? HANDS_FREE_LISTENING : PHASE_LABEL[phase]}
        {listening ? (
          <span className="ml-auto font-mono text-xs text-muted-foreground">
            {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
          </span>
        ) : null}
      </p>

      {listening || phase === "finishing" ? (
        <p
          className={cn(
            "min-h-12 whitespace-pre-wrap rounded-lg border bg-card/60 px-3 py-2 text-sm",
            heard ? "" : "italic text-muted-foreground",
          )}
          data-testid="voice-transcript"
        >
          {heard || "Your words appear here as you speak."}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!listening} onClick={() => void finish()}>
          <Square aria-hidden /> Finish answer
        </Button>
        <Button size="sm" variant="ghost" onClick={cancel}>
          <X aria-hidden /> {handsFree ? "Stop hands-free" : "Cancel"}
        </Button>
      </div>
    </div>
  )
}
